import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getClubs } from '../../services/clubService';
import { trackClubsDiscoveryView, trackClubView } from '../../utils/clubStoreAnalytics';
import { Club } from '../../types/clubs';
import { MagnifyingGlassIcon, UserGroupIcon } from '@heroicons/react/24/outline';

const ClubsDiscoveryPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadClubs();
    // Track analytics when page is viewed
    if (user?.id) {
      trackClubsDiscoveryView(user.role || undefined).catch(console.error);
    }
  }, [user?.id]);

  useEffect(() => {
    // Debounce search
    const timer = setTimeout(() => {
      if (!loading) {
        loadClubs();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadClubs = async () => {
    try {
      setError(null);
      setLoading(true);
      const fetchedClubs = await getClubs({
        search: searchQuery || undefined,
        city: user?.preferredCity || undefined,
      });
      setClubs(fetchedClubs);
      // Clear error if we successfully got an empty array (no clubs available)
      if (fetchedClubs.length === 0) {
        setError(null);
      }
    } catch (err: any) {
      // This should rarely happen now since getClubs handles errors internally
      console.error('Error fetching clubs:', err);
      setError(null); // Don't show error, just show empty state
      setClubs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClubClick = async (club: Club) => {
    if (user?.id) {
      await trackClubView(club.id, 'ClubsDiscovery', user.role || undefined).catch(console.error);
    }
  };

  if (loading && clubs.length === 0) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center">{t('common.loading') || 'Loading clubs...'}</div>
      </div>
    );
  }

  if (error && clubs.length === 0) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadClubs}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            {t('common.retry') || 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('clubs.searchPlaceholder') || 'Search clubs...'}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <span className="text-gray-400 hover:text-gray-600">×</span>
            </button>
          )}
        </div>
      </div>

      {/* Clubs List */}
      {clubs.length === 0 ? (
        <div className="text-center py-12">
          <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {searchQuery 
              ? (t('clubs.noClubsFound') || 'No clubs found')
              : (t('clubs.noClubs') || 'No clubs available')}
          </h3>
          {!searchQuery && (
            <p className="mt-1 text-sm text-gray-500">
              {t('clubs.checkBackLater') || 'Check back later for new clubs'}
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clubs.map((club) => (
            <Link
              key={club.id}
              to={`/clubs/${club.id}`}
              onClick={() => handleClubClick(club)}
              className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow p-6"
            >
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{club.name}</h3>
                {club.isActive && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {t('common.active') || 'Active'}
                  </span>
                )}
              </div>
              {club.description && (
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{club.description}</p>
              )}
              {club.location && (
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>{club.location}</span>
                </div>
              )}
              {club.memberCount !== undefined && (
                <div className="flex items-center text-sm text-primary-600 font-medium">
                  <UserGroupIcon className="h-4 w-4 mr-1" />
                  <span>{club.memberCount} {t('clubs.members') || 'members'}</span>
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClubsDiscoveryPage;
